import { dehydrate, type DehydratedState, type QueryClient } from '@tanstack/react-query';
import {
  AUCUN_DROIT,
  RESSOURCES_HORS_LIGNE,
  DUREE_MAX_HORS_LIGNE_HEURES,
  type RessourceHorsLigne,
} from '@artosera/shared';
import type { SessionUtilisateur } from '../api/types.js';
import { estConfinee } from '../auth/confinement.js';
import { PREFIXE_METIER } from '../auth/identite.js';
import { CLE_SESSION } from '../auth/queryClient.js';
import { chiffrer, creerCle, dechiffrer } from './chiffrement.js';
import { memeGarde, type GardeStockage, type MagasinHorsLigne } from './magasin.js';

export const FORMAT_HORS_LIGNE = 1;
export const TAILLE_MAX_OCTETS = 25 * 1024 * 1024;

export interface InstantaneHorsLigne {
  readonly garde: GardeStockage;
  readonly format: number;
  readonly build: string;
  readonly autorisation: string;
  readonly compteId: string;
  readonly galerieSlug: string;
  readonly enregistreLe: string;
  readonly verifieLe: string;
  readonly expireLe: string;
  readonly session: SessionUtilisateur;
  readonly requetes: DehydratedState;
}

export type IssueEnregistrement = 'enregistre' | 'efface' | 'trop_volumineux' | 'sans_verification';

export function idInstantane(compteId: string, slug: string): string {
  return `${compteId}:${slug}`;
}

export function estPersistable(
  cle: readonly unknown[],
  compteId: string,
  slug: string,
  perimetre: readonly RessourceHorsLigne[],
): boolean {
  const [prefixe, compte, galerie, ressource, nature] = cle;
  return (
    prefixe === PREFIXE_METIER &&
    compte === compteId &&
    galerie === slug &&
    typeof ressource === 'string' &&
    (RESSOURCES_HORS_LIGNE as readonly string[]).includes(ressource) &&
    (perimetre as readonly string[]).includes(ressource) &&
    (nature === 'liste' || nature === 'fiche')
  );
}

// Schémas positifs : un nouveau champ serveur reste absent par défaut, à chaque profondeur.
type Selection = true | { readonly [cle: string]: Selection } | readonly [Selection];
const champs = (...noms: string[]): Record<string, Selection> =>
  Object.fromEntries(noms.map((n) => [n, true]));
const bilingue = champs('fr', 'en');
const lieu = champs('id', 'nom', 'type');
const oeuvre: Selection = {
  ...champs(
    'id',
    'titre',
    'slug',
    'slugPublie',
    'artisteId',
    'artisteNom',
    'annee',
    'type',
    'numeroInventaire',
    'prixPublicCentimes',
    'devise',
    'regimeTva',
    'droitDeSuite',
    'statut',
    'localisationId',
    'mediaPrincipalId',
    'visibleSurLeSite',
    'prixVisible',
    'creeLe',
    'modifieLe',
  ),
  technique: bilingue,
  description: bilingue,
  etiquettes: [true],
  dimensions: champs('hauteur', 'largeur', 'profondeur', 'diametre', 'uniteOrigine'),
  tirage: champs('taille', 'epreuvesArtiste', 'epreuvesEssai', 'exemplairesSuivis', 'vendus'),
};
const artiste: Selection = {
  ...champs(
    'id',
    'nom',
    'slug',
    'naissance',
    'nationalite',
    'portraitId',
    'ordre',
    'visibleSurLeSite',
    'archive',
    'creeLe',
    'modifieLe',
    'nombreOeuvres',
  ),
  biographie: bilingue,
  cv: bilingue,
  presse: bilingue,
  ordreOeuvres: [true],
  representation: champs('representee', 'depuis', 'jusquau', 'exclusivite'),
};
const media: Selection = {
  ...champs('id', 'type', 'credit', 'ordre'),
  legende: bilingue,
  // Pas de clé de stockage ni d'URL signée durable dans l'instantané.
};
const exposition: Selection = {
  ...champs(
    'id',
    'type',
    'slug',
    'slugPublie',
    'debutLe',
    'finLe',
    'lieuId',
    'stand',
    'etat',
    'visibleSurLeSite',
    'archive',
    'creeLe',
    'modifieLe',
  ),
  titre: bilingue,
  sousTitre: bilingue,
  texte: bilingue,
  artistes: [true],
  oeuvres: [true],
  vernissage: { ...champs('date', 'debut', 'fin'), note: bilingue },
};
const consultation: Selection = {
  ...champs('id', 'titre', 'prixArtisteVisible'),
  oeuvre,
  oeuvres: [oeuvre],
  artiste,
  artistes: [artiste],
  exposition,
  expositions: [exposition],
  medias: [media],
  vuesAccrochage: [media],
  localisation: lieu,
  lieu,
  editions: [champs('id', 'numero', 'type', 'statut', 'prixCentimes')],
  pagination: champs('page', 'parPage', 'total', 'pages'),
};
function selectionner(valeur: unknown, selection: Selection): unknown {
  if (valeur === null) return null;
  if (selection === true)
    return ['string', 'number', 'boolean'].includes(typeof valeur) ? valeur : undefined;
  if (Array.isArray(selection))
    return Array.isArray(valeur) ? valeur.map((v) => selectionner(v, selection[0]!)) : undefined;
  if (typeof valeur !== 'object' || Array.isArray(valeur)) return undefined;
  const resultat: Record<string, unknown> = {};
  for (const [cle, schema] of Object.entries(selection)) {
    if (!Object.hasOwn(valeur, cle)) continue;
    const contenu = selectionner((valeur as Record<string, unknown>)[cle], schema);
    if (contenu !== undefined) resultat[cle] = cle === 'prixArtisteVisible' ? false : contenu;
  }
  return resultat;
}
export function assainir(valeur: unknown): unknown {
  return selectionner(valeur, consultation);
}

/** La session que la régie restitue sans réseau : cette galerie, en lecture seule. */
export function sessionDeConsultation(
  session: SessionUtilisateur,
  slug: string,
): SessionUtilisateur {
  return {
    compte: {
      id: session.compte.id,
      email: session.compte.email,
      nom: session.compte.nom,
      langue: session.compte.langue,
      deuxiemeFacteurActive: session.compte.deuxiemeFacteurActive,
      graceMfaJusquau: null,
      rolePlateforme: null,
      groupes: [],
    },
    consultationHorsLigne: true,
    galeries: session.galeries
      .filter((galerie) => galerie.slug === slug)
      .map((galerie) => ({
        id: galerie.id,
        slug: galerie.slug,
        nom: galerie.nom,
        etat: galerie.etat,
        langue: galerie.langue,
        support: false,
        expireLe: galerie.expireLe,
        horsLigne: galerie.horsLigne
          ? {
              perimetre: RESSOURCES_HORS_LIGNE.filter((r) =>
                galerie.horsLigne?.perimetre.includes(r),
              ),
              dureeMaxHeures: galerie.horsLigne.dureeMaxHeures,
            }
          : null,
        role: 'lecture',
        droits: AUCUN_DROIT,
        modules: ['oeuvres', 'artistes', 'expositions', 'application-hors-ligne'],
        actions: LECTURES.filter(
          (action, index) =>
            galerie.actions.includes(action) &&
            galerie.horsLigne?.perimetre.includes(RESSOURCES_HORS_LIGNE[index]!),
        ),
      })),
  };
}

export interface DependancesPersistance {
  readonly magasin: MagasinHorsLigne;
  readonly moteur: Crypto;
  readonly maintenant: () => Date;
  readonly build: string;
  /** Tests seulement : simule un format futur. */
  readonly format?: number;
}

export interface PersistanceHorsLigne {
  commencerVerification(): Promise<number>;
  estCourant(instantane: InstantaneHorsLigne): Promise<boolean>;
  surveiller(instantane: InstantaneHorsLigne, invalide: () => void): () => void;
  harmoniser(
    session: SessionUtilisateur,
    slug: string | null,
    droitsChanges: boolean,
    generationAvantRequete?: number,
  ): Promise<void>;
  enregistrer(client: QueryClient, slug: string): Promise<IssueEnregistrement>;
  lire(slug: string | null): Promise<InstantaneHorsLigne | null>;
  purgerTout(): Promise<void>;
  etat(): Promise<{
    readonly enregistreLe: string;
    readonly expireLe: string;
    readonly requetes: number;
  } | null>;
}

const LECTURES = ['artworks:read', 'artists:read', 'exhibitions:read'] as const;

export function creerPersistance(dep: DependancesPersistance): PersistanceHorsLigne {
  const format = dep.format ?? FORMAT_HORS_LIGNE;
  const verifications = new Map<string, { date: Date; empreinte: string; garde: GardeStockage }>();
  let revision = 0;
  let file: Promise<unknown> = Promise.resolve();
  // Ordre strict : une purge attend les écritures commencées et les annule
  // immédiatement par révision. Aucune écriture ne peut la dépasser.
  function ordonner<T>(faire: () => Promise<T>): Promise<T> {
    const suite = file.then(faire, faire);
    file = suite.catch(() => undefined);
    return suite;
  }
  function autorisation(session: SessionUtilisateur, slug: string) {
    // Une session confinée (CGU à accepter, double authentification à
    // activer) ne produit ni ne rafraîchit rien — et `harmoniser` efface ce
    // qui était gardé.
    if (estConfinee(session)) return null;
    const galerie = session.galeries.find((g) => g.slug === slug);
    if (
      !galerie ||
      galerie.support ||
      galerie.etat !== 'active' ||
      !galerie.modules?.includes('application-hors-ligne') ||
      !galerie.horsLigne
    )
      return null;
    const duree = galerie.horsLigne.dureeMaxHeures;
    if (!Number.isFinite(duree) || duree <= 0) return null;
    const perimetre = RESSOURCES_HORS_LIGNE.filter(
      (r, i) => galerie.horsLigne?.perimetre.includes(r) && galerie.actions.includes(LECTURES[i]!),
    );
    if (!perimetre.length) return null;
    return { perimetre, dureeMaxHeures: Math.min(duree, DUREE_MAX_HORS_LIGNE_HEURES) };
  }
  function empreinte(session: SessionUtilisateur, slug: string): string {
    const g = session.galeries.find((g) => g.slug === slug);
    return JSON.stringify([
      g?.role,
      g?.droits,
      [...(g?.actions ?? [])].sort(),
      [...(g?.modules ?? [])].sort(),
      g?.support,
      g?.etat,
      autorisation(session, slug),
    ]);
  }
  async function purger(id: string): Promise<void> {
    verifications.delete(id);
    await dep.magasin.supprimer(id);
  }
  async function lireId(id: string): Promise<InstantaneHorsLigne | null> {
    const [cle, chiffre] = await Promise.all([
      dep.magasin.lireCle(id),
      dep.magasin.lireInstantane(id),
    ]);
    if (!cle || !chiffre) {
      await purger(id);
      return null;
    }
    try {
      const instantane = (await dechiffrer(dep.moteur, cle, id, chiffre)) as InstantaneHorsLigne;
      const expiration = Date.parse(instantane.expireLe);
      const verification = Date.parse(instantane.verifieLe);
      const maintenant = dep.maintenant().getTime();
      if (
        instantane.format !== format ||
        idInstantane(instantane.compteId, instantane.galerieSlug) !== id ||
        instantane.session.compte.id !== instantane.compteId ||
        !Number.isFinite(expiration) ||
        !Number.isFinite(verification) ||
        verification > maintenant ||
        expiration <= maintenant ||
        expiration > verification + DUREE_MAX_HORS_LIGNE_HEURES * 3_600_000 ||
        !autorisation(instantane.session, instantane.galerieSlug)
      )
        throw new Error('instantane invalide');
      // Ne jamais hydrater de métadonnées ou une mutation provenant du cache.
      const reglage = autorisation(instantane.session, instantane.galerieSlug)!;
      const queries = instantane.requetes.queries.filter((q) =>
        estPersistable(q.queryKey, instantane.compteId, instantane.galerieSlug, reglage.perimetre),
      );
      return {
        ...instantane,
        session: sessionDeConsultation(instantane.session, instantane.galerieSlug),
        requetes: {
          mutations: [],
          queries: queries.map((q) => ({
            ...q,
            state: { ...q.state, data: assainir(q.state.data) },
          })),
        },
      };
    } catch {
      await purger(id);
      return null;
    }
  }
  async function lireUnique(slug: string | null): Promise<InstantaneHorsLigne | null> {
    const garde = await dep.magasin.controle();
    const ids = await dep.magasin.identifiants();
    if (ids.length !== 1) {
      for (const id of ids) await purger(id);
      return null;
    }
    const id = ids[0]!;
    if (slug !== null && !id.endsWith(`:${slug}`)) return null;
    if (garde.generation > 0 && garde.proprietaire !== id) return null;
    const lu = await lireId(id);
    if (!lu || !memeGarde(garde, await dep.magasin.controle())) return null;
    return { ...lu, garde };
  }
  async function estCourant(instantane: InstantaneHorsLigne): Promise<boolean> {
    return memeGarde(instantane.garde, await dep.magasin.controle());
  }
  return {
    commencerVerification: async () => (await dep.magasin.controle()).generation,
    estCourant,
    surveiller(instantane, invalide) {
      let arrete = false;
      const verifier = () => {
        void estCourant(instantane)
          .then((valide) => {
            if (!arrete && !valide) {
              arrete = true;
              invalide();
            }
          })
          .catch(() => {
            if (!arrete) {
              arrete = true;
              invalide();
            }
          });
      };
      const stopper = dep.magasin.surveiller(verifier);
      verifier();
      return () => {
        arrete = true;
        stopper();
      };
    },
    harmoniser(session, slug, droitsChanges, generationAvantRequete) {
      const capture = ++revision;
      verifications.clear();
      // Capturer avant l'attente de la file, ou avant /auth/me en production :
      // une réponse ancienne ne peut pas réautoriser après une purge ailleurs.
      const generation =
        generationAvantRequete === undefined
          ? dep.magasin.controle().then((g) => g.generation)
          : Promise.resolve(generationAvantRequete);
      return ordonner(async () => {
        const attendue = await generation;
        if (capture !== revision) return;
        verifications.clear();
        const courante = await dep.magasin.controle();
        if (courante.generation !== attendue) return;
        const galerie =
          session.galeries.find(
            (g) =>
              g.slug !== null &&
              (slug
                ? g.slug === slug
                : courante.proprietaire === idInstantane(session.compte.id, g.slug)) &&
              autorisation(session, g.slug),
          ) ??
          (slug === null
            ? session.galeries.find((g) => g.slug !== null && autorisation(session, g.slug))
            : undefined);
        if (!galerie?.slug || session.consultationHorsLigne) {
          await dep.magasin.invalider();
          return;
        }
        const id = idInstantane(session.compte.id, galerie.slug);
        const empreinteActuelle = empreinte(session, galerie.slug);
        const garde = await dep.magasin.autoriser(attendue, id, empreinteActuelle, droitsChanges);
        if (!garde || capture !== revision) return;
        verifications.set(id, { date: dep.maintenant(), empreinte: empreinteActuelle, garde });
      });
    },
    enregistrer(client, slug) {
      const capture = revision;
      return ordonner(async () => {
        if (capture !== revision) return 'sans_verification';
        const session = client.getQueryData<SessionUtilisateur>(CLE_SESSION);
        if (!session || session.consultationHorsLigne) return 'sans_verification';
        const id = idInstantane(session.compte.id, slug);
        const reglage = autorisation(session, slug);
        if (!reglage) {
          await purger(id);
          return 'efface';
        }
        const verification = verifications.get(id);
        if (!verification || verification.empreinte !== empreinte(session, slug))
          return 'sans_verification';
        const expire = verification.date.getTime() + reglage.dureeMaxHeures * 3_600_000;
        if (expire <= dep.maintenant().getTime()) {
          await purger(id);
          return 'sans_verification';
        }
        const brut = dehydrate(client, {
          shouldDehydrateMutation: () => false,
          shouldDehydrateQuery: (q) =>
            q.state.status === 'success' &&
            estPersistable(q.queryKey, session.compte.id, slug, reglage.perimetre),
        });
        const requetes: DehydratedState = {
          mutations: [],
          queries: brut.queries.map((q) => ({
            queryKey: q.queryKey,
            queryHash: q.queryHash,
            dehydratedAt: q.dehydratedAt,
            state: {
              ...q.state,
              data: assainir(q.state.data),
              error: null,
              fetchFailureReason: null,
              fetchStatus: 'idle',
            },
          })),
        };
        const instantane: InstantaneHorsLigne = {
          garde: verification.garde,
          format,
          build: dep.build,
          autorisation: empreinte(session, slug),
          compteId: session.compte.id,
          galerieSlug: slug,
          enregistreLe: dep.maintenant().toISOString(),
          verifieLe: verification.date.toISOString(),
          expireLe: new Date(expire).toISOString(),
          session: sessionDeConsultation(session, slug),
          requetes,
        };
        if (new TextEncoder().encode(JSON.stringify(instantane)).byteLength > TAILLE_MAX_OCTETS)
          return 'trop_volumineux';
        // La clé reste locale jusqu'à la publication atomique. Une purge dans
        // un autre onglet interdit aussi bien sa création que le chiffré.
        const cle = await creerCle(dep.moteur);
        const chiffre = await chiffrer(dep.moteur, cle, id, instantane);
        if (capture !== revision) return 'sans_verification';
        if (!(await dep.magasin.publier(verification.garde, cle, chiffre))) {
          verifications.clear();
          return 'sans_verification';
        }
        return 'enregistre';
      });
    },
    lire(slug) {
      return ordonner(() => lireUnique(slug));
    },
    purgerTout() {
      ++revision;
      verifications.clear();
      // Ne pas attendre une opération de chiffrement locale : IndexedDB
      // sérialise lui-même cette invalidation avec toutes les publications.
      return dep.magasin.invalider();
    },
    etat() {
      return ordonner(async () => {
        const i = await lireUnique(null);
        return i
          ? {
              enregistreLe: i.enregistreLe,
              expireLe: i.expireLe,
              requetes: i.requetes.queries.length,
            }
          : null;
      });
    },
  };
}
